'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

type RegistrationFormData = {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  primaryPlatform: string;
  socialUsername: string;
  socialProfileUrl: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
  ageConfirmed: boolean;
};

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh',
];

const platforms = [
  { value: 'facebook', label: 'Facebook', icon: 'GlobeAltIcon' },
  { value: 'instagram', label: 'Instagram', icon: 'CameraIcon' },
  { value: 'youtube', label: 'YouTube', icon: 'VideoCameraIcon' },
  { value: 'tiktok', label: 'TikTok', icon: 'MusicalNoteIcon' },
  { value: 'multiple', label: 'Multiple Platforms', icon: 'DevicePhoneMobileIcon' },
];

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function RegistrationForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegistrationFormData>({
    mode: 'onBlur',
  });

  const password = watch('password');

  // BACKEND INTEGRATION POINT: POST /api/members/register
  const onSubmit = async (data: RegistrationFormData) => {
    setSubmitStatus('loading');
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Registration data:', data);
    setSubmitStatus('success');
  };

  if (submitStatus === 'success') {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 lg:p-10 text-center fade-in">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
          <Icon name="CheckCircleIcon" size={44} variant="solid" className="text-secondary" />
        </div>
        <h2 className="text-2xl font-700 text-foreground mb-2">Registration Successful!</h2>
        <p className="text-muted-foreground text-base mb-6">
          Welcome to FCEP! Your member account has been created. Please complete your
          document verification to access all programs.
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 text-left">
          <div className="flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={20} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-600 text-sm mb-1">Next Step: Document Verification</p>
              <p className="text-amber-700 text-sm">
                Upload your Aadhaar card, PAN card, selfie, and social media profile proof.
                Pay the one-time ₹500 verification fee to activate your FCEP membership.
              </p>
            </div>
          </div>
        </div>

        <Link href="/document-verification-page" className="btn-primary w-full justify-center">
          <Icon name="DocumentArrowUpIcon" size={20} variant="outline" />
          Complete Document Verification
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8">
      <div className="mb-6">
        <h2 className="text-xl font-700 text-foreground">Member Registration Form</h2>
        <p className="text-muted-foreground text-sm mt-1">
          All fields marked with <span className="text-danger">*</span> are required.
          Registration is completely free.
        </p>
      </div>

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 flex items-start gap-3">
          <Icon name="ExclamationCircleIcon" size={18} variant="solid" className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-danger text-sm">
            Registration failed. Please check your details and try again.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Section 1: Personal Information */}
        <div className="mb-6 pb-6 border-b border-border">
          <h3 className="text-base font-600 text-primary mb-4 flex items-center gap-2">
            <Icon name="UserIcon" size={18} variant="outline" />
            Personal Information
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Full Name */}
            <div className="sm:col-span-2">
              <label htmlFor="fullName" className="form-label">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                placeholder="Enter your full name as per Aadhaar"
                className={`input-field ${errors.fullName ? 'error' : ''}`}
                {...register('fullName', {
                  required: 'Full name is required',
                  minLength: { value: 3, message: 'Name must be at least 3 characters' },
                  pattern: {
                    value: /^[a-zA-Z\s.]+$/,
                    message: 'Name should contain only letters and spaces',
                  },
                })}
              />
              {errors.fullName && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder="yourname@gmail.com"
                className={`input-field ${errors.email ? 'error' : ''}`}
                {...register('email', {
                  required: 'Email address is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {errors.email && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="form-label">
                Mobile Number <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-500">
                  +91
                </span>
                <input
                  id="phone"
                  type="tel"
                  placeholder="9876543210"
                  className={`input-field pl-12 ${errors.phone ? 'error' : ''}`}
                  {...register('phone', {
                    required: 'Mobile number is required',
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: 'Enter a valid 10-digit Indian mobile number',
                    },
                  })}
                />
              </div>
              {errors.phone && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" className="form-label">
                City / District <span className="text-danger">*</span>
              </label>
              <input
                id="city"
                type="text"
                placeholder="e.g. Malda, Kolkata, Howrah"
                className={`input-field ${errors.city ? 'error' : ''}`}
                {...register('city', {
                  required: 'City is required',
                })}
              />
              {errors.city && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.city.message}
                </p>
              )}
            </div>

            {/* State */}
            <div>
              <label htmlFor="state" className="form-label">
                State <span className="text-danger">*</span>
              </label>
              <select
                id="state"
                className={`input-field ${errors.state ? 'error' : ''}`}
                {...register('state', { required: 'Please select your state' })}
              >
                <option value="">Select State</option>
                {indianStates.map((state) => (
                  <option key={`state-${state.replace(/\s/g, '-').toLowerCase()}`} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.state.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Social Media Information */}
        <div className="mb-6 pb-6 border-b border-border">
          <h3 className="text-base font-600 text-primary mb-4 flex items-center gap-2">
            <Icon name="DevicePhoneMobileIcon" size={18} variant="outline" />
            Social Media Information
          </h3>

          <div className="grid grid-cols-1 gap-4">
            {/* Primary Platform */}
            <div>
              <label htmlFor="primaryPlatform" className="form-label">
                Primary Social Media Platform <span className="text-danger">*</span>
              </label>
              <p className="form-helper mb-2">
                Select the platform you use most or want to focus on for earning.
              </p>
              <select
                id="primaryPlatform"
                className={`input-field ${errors.primaryPlatform ? 'error' : ''}`}
                {...register('primaryPlatform', {
                  required: 'Please select a platform',
                })}
              >
                <option value="">Select Platform</option>
                {platforms.map((p) => (
                  <option key={`platform-opt-${p.value}`} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              {errors.primaryPlatform && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.primaryPlatform.message}
                </p>
              )}
            </div>

            {/* Social Media Username */}
            <div>
              <label htmlFor="socialUsername" className="form-label">
                Social Media Username / Page Name <span className="text-danger">*</span>
              </label>
              <p className="form-helper mb-2">
                Your username on your primary platform (e.g. @yourname or Page Name)
              </p>
              <input
                id="socialUsername"
                type="text"
                placeholder="e.g. @priya.sharma.official or Priya Sharma Page"
                className={`input-field ${errors.socialUsername ? 'error' : ''}`}
                {...register('socialUsername', {
                  required: 'Social media username is required',
                  minLength: { value: 3, message: 'Username must be at least 3 characters' },
                })}
              />
              {errors.socialUsername && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.socialUsername.message}
                </p>
              )}
            </div>

            {/* Profile URL */}
            <div>
              <label htmlFor="socialProfileUrl" className="form-label">
                Social Media Profile URL
              </label>
              <p className="form-helper mb-2">
                Paste the direct link to your profile, page, or channel (optional but recommended)
              </p>
              <input
                id="socialProfileUrl"
                type="url"
                placeholder="https://www.facebook.com/yourpage"
                className={`input-field ${errors.socialProfileUrl ? 'error' : ''}`}
                {...register('socialProfileUrl', {
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: 'Enter a valid URL starting with http:// or https://',
                  },
                })}
              />
              {errors.socialProfileUrl && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.socialProfileUrl.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Account Security */}
        <div className="mb-6 pb-6 border-b border-border">
          <h3 className="text-base font-600 text-primary mb-4 flex items-center gap-2">
            <Icon name="LockClosedIcon" size={18} variant="outline" />
            Account Security
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Password */}
            <div>
              <label htmlFor="password" className="form-label">
                Password <span className="text-danger">*</span>
              </label>
              <p className="form-helper mb-2">
                Minimum 8 characters with letters and numbers
              </p>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  className={`input-field pr-10 ${errors.password ? 'error' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                    pattern: {
                      value: /^(?=.*[A-Za-z])(?=.*\d).+$/,
                      message: 'Password must include letters and numbers',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                    size={18}
                    variant="outline"
                  />
                </button>
              </div>
              {errors.password && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <p className="form-helper mb-2">Re-enter your password to confirm</p>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Repeat your password"
                  className={`input-field pr-10 ${errors.confirmPassword ? 'error' : ''}`}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match',
                  })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon
                    name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                    size={18}
                    variant="outline"
                  />
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="form-error">
                  <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Section 4: Agreements */}
        <div className="mb-6">
          <h3 className="text-base font-600 text-primary mb-4 flex items-center gap-2">
            <Icon name="DocumentTextIcon" size={18} variant="outline" />
            Agreements
          </h3>

          <div className="space-y-3">
            {/* Terms */}
            <div className="flex items-start gap-3">
              <input
                id="termsAccepted"
                type="checkbox"
                className="mt-1 w-4 h-4 rounded border-input accent-primary cursor-pointer flex-shrink-0"
                {...register('termsAccepted', {
                  required: 'You must accept the terms and conditions to register',
                })}
              />
              <div>
                <label htmlFor="termsAccepted" className="text-sm text-foreground cursor-pointer">
                  I agree to the{' '}
                  <Link href="/about" className="text-primary hover:underline font-500">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link href="/about" className="text-primary hover:underline font-500">
                    Privacy Policy
                  </Link>{' '}
                  of FCEP and CMX Marketing and Co.{' '}
                  <span className="text-danger">*</span>
                </label>
                {errors.termsAccepted && (
                  <p className="form-error mt-1">
                    <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                    {errors.termsAccepted.message}
                  </p>
                )}
              </div>
            </div>

            {/* Age */}
            <div className="flex items-start gap-3">
              <input
                id="ageConfirmed"
                type="checkbox"
                className="mt-1 w-4 h-4 rounded border-input accent-primary cursor-pointer flex-shrink-0"
                {...register('ageConfirmed', {
                  required: 'You must confirm you are 18 years or older',
                })}
              />
              <div>
                <label htmlFor="ageConfirmed" className="text-sm text-foreground cursor-pointer">
                  I confirm that I am 18 years of age or older and the information
                  provided is accurate and truthful.{' '}
                  <span className="text-danger">*</span>
                </label>
                {errors.ageConfirmed && (
                  <p className="form-error mt-1">
                    <Icon name="ExclamationCircleIcon" size={13} variant="solid" />
                    {errors.ageConfirmed.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Verification Fee Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={18} variant="solid" className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-600 text-sm">Document Verification Required After Registration</p>
              <p className="text-amber-700 text-xs mt-1">
                After registering, you will need to upload your documents (Aadhaar, PAN, selfie, social media proof)
                and pay a one-time ₹500 verification fee to activate your FCEP membership.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitStatus === 'loading'}
          className="btn-primary w-full justify-center text-base disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
          style={{ padding: '0.875rem 1.5rem' }}
        >
          {submitStatus === 'loading' ? (
            <>
              <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
              Creating Your Account...
            </>
          ) : (
            <>
              <Icon name="UserPlusIcon" size={20} variant="outline" />
              Register as FCEP Member — Free
            </>
          )}
        </button>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Already a member?{' '}
          <Link href="/document-verification-page" className="text-primary font-600 hover:underline">
            Login to verify documents
          </Link>
        </p>
      </form>
    </div>
  );
}